import { useEffect, useRef } from 'react';
import { getCachedAudio } from './musicCache';
import { useMusic as useMusicContext } from '../music/MusicContext';
import {
  getCurrentPlayingAudio,
  setCurrentPlayingAudio,
  getCurrentFadeOutPromise,
  setCurrentFadeOutPromise,
  acquireTrackStartLock,
  stopAllAudio,
  registerAudioElement,
  unregisterAudioElement
} from './musicState';

/**
 * Custom hook for managing background music with fade in/out effects
 * Uses cached Audio objects for better performance
 * @param {string} trackPath - Path to the music file (e.g., '/music/track.mp3')
 * @param {number} startTime - Optional start time in seconds (default: 0)
 * @param {number} fadeDuration - Fade in/out duration in milliseconds (default: 2000)
 */
export function useMusic(trackPath, startTime = 0, fadeDuration = 2000) {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const userInteractionHandlerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const healthCheckIntervalRef = useRef(null);
  const retryCountRef = useRef(0);
  const lastRetryTimeRef = useRef(0);
  const trackEndHandlerRef = useRef(null);
  const { musicEnabled, musicVolume } = useMusicContext();

  useEffect(() => {
    if (!trackPath) return;
    
    // If music is disabled, stop any playing audio
    if (!musicEnabled) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.volume = 0;
        audioRef.current.currentTime = 0;
      }
      return;
    }

    // Get cached audio element (reuses existing if available)
    const audio = getCachedAudio(trackPath);
    
    // Register this audio element for tracking
    registerAudioElement(audio);
    
    // Reset audio state for this use
    audio.volume = 0; // Start at 0 for fade in
    audio.loop = true;
    
    // Set start time if specified
    if (startTime > 0) {
      audio.currentTime = startTime;
    }

    audioRef.current = audio;
    let fadeIntervalId = null;
    let releaseLock = null;

    // Fade out function that returns a promise
    const fadeOutAudio = (audioToFade, fadeDurationMs) => {
      return new Promise((resolve) => {
        if (!audioToFade || audioToFade.paused || audioToFade.volume === 0) {
          // Already stopped or at 0 volume, resolve immediately
          resolve();
          return;
        }

        const steps = 50;
        const currentVolume = audioToFade.volume;
        const volumeStep = currentVolume / steps;
        const stepDuration = fadeDurationMs / steps;
        let currentStep = 0;

        const fadeOutInterval = setInterval(() => {
          currentStep++;
          const newVolume = Math.max(currentVolume - (volumeStep * currentStep), 0);
          audioToFade.volume = newVolume;

          if (currentStep >= steps || newVolume <= 0) {
            clearInterval(fadeOutInterval);
            audioToFade.pause();
            audioToFade.currentTime = 0;
            audioToFade.volume = 0;
            resolve();
          }
        }, stepDuration);
      });
    };

    // Fade in
    const fadeIn = () => {
      if (isPlayingRef.current) return; // Already fading in
      
      const targetVolume = musicVolume;
      const steps = 50;
      const volumeStep = targetVolume / steps;
      const stepDuration = fadeDuration / steps;
      let currentStep = 0;

      fadeIntervalId = setInterval(() => {
        currentStep++;
        const newVolume = Math.min(volumeStep * currentStep, targetVolume);
        audio.volume = newVolume;

        if (currentStep >= steps) {
          clearInterval(fadeIntervalId);
          fadeIntervalId = null;
          audio.volume = targetVolume;
        }
      }, stepDuration);

      fadeIntervalRef.current = fadeIntervalId;
      isPlayingRef.current = true;
    };

    // Start the new track after waiting for any ongoing fade-out
    const startNewTrack = async () => {
      // CRITICAL: Acquire lock to prevent concurrent track starts
      releaseLock = await acquireTrackStartLock();
      
      try {
        // Double-check: Stop ALL audio as a safety measure
        stopAllAudio();
        
        // Wait a tiny bit to ensure all stops are processed
        await new Promise(resolve => setTimeout(resolve, 10));

        // If there's a currently playing audio that's different from the new one, fade it out
        const currentAudio = getCurrentPlayingAudio();
        if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
          const fadeOutPromise = fadeOutAudio(currentAudio, fadeDuration);
          setCurrentFadeOutPromise(fadeOutPromise);
          await fadeOutPromise;
        }

        // Now set the new audio as current and start it
        setCurrentPlayingAudio(audio);
      } catch (error) {
        // Release lock on error
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        throw error;
      }
    };

    // Start playing function with retry logic
    const startPlaying = async (isRetry = false) => {
      // Don't start if already playing (unless it's a retry)
      if (!isRetry && isPlayingRef.current && !audio.paused && getCurrentPlayingAudio() === audio) {
        return;
      }
      
      try {
        // Wait for fade-out and start new track (acquires lock)
        await startNewTrack();

        // CRITICAL: Verify no other audio is playing before starting
        const currentAudio = getCurrentPlayingAudio();
        if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
          // Emergency stop if something else is playing
          stopAllAudio();
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Reset start time if specified (in case audio was paused)
        if (startTime > 0 && audio.currentTime < startTime) {
          audio.currentTime = startTime;
        }
        
        // Ensure audio is stopped before playing
        if (!audio.paused) {
          audio.pause();
        }
        audio.volume = 0;
        
        await audio.play();
        fadeIn();
        
        // Reset retry count on success
        retryCountRef.current = 0;
        lastRetryTimeRef.current = 0;
        
        // Release lock after successfully starting
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        
        // Remove user interaction listener if it was set up
        if (userInteractionHandlerRef.current) {
          document.removeEventListener('click', userInteractionHandlerRef.current);
          document.removeEventListener('touchstart', userInteractionHandlerRef.current);
          document.removeEventListener('keydown', userInteractionHandlerRef.current);
          userInteractionHandlerRef.current = null;
        }

        // MOBILE FIX: Add ended event listener as backup (loop might not work on mobile)
        trackEndHandlerRef.current = async () => {
          try {
            // Reset to start time if specified, otherwise to beginning
            if (startTime > 0) {
              audio.currentTime = startTime;
            } else {
              audio.currentTime = 0;
            }
            
            // Restart playback
            if (audio.paused) {
              await audio.play();
            } else {
              audio.pause();
              if (startTime > 0) {
                audio.currentTime = startTime;
              } else {
                audio.currentTime = 0;
              }
              await audio.play();
            }
          } catch (error) {
            // Handle autoplay restrictions
            if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
              console.log('Music: Autoplay blocked on track end, waiting for user interaction...');
              // Health check will handle restart
            } else {
              console.error('Error restarting music on track end:', error);
              scheduleRetry();
            }
          }
        };
        
        audio.addEventListener('ended', trackEndHandlerRef.current);
      } catch (error) {
        // Release lock on error
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        
        // Autoplay was blocked - set up user interaction listener
        if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
          // Only set up listener if we don't already have one
          if (!userInteractionHandlerRef.current) {
            const handleUserInteraction = async () => {
              try {
                await startNewTrack();
                
                // CRITICAL: Verify no other audio is playing
                const currentAudio = getCurrentPlayingAudio();
                if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
                  stopAllAudio();
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
                
                if (startTime > 0 && audio.currentTime < startTime) {
                  audio.currentTime = startTime;
                }
                
                if (!audio.paused) {
                  audio.pause();
                }
                audio.volume = 0;
                
                await audio.play();
                fadeIn();
                
                // Reset retry count on success
                retryCountRef.current = 0;
                lastRetryTimeRef.current = 0;
                
                if (releaseLock) {
                  releaseLock();
                  releaseLock = null;
                }
              } catch (err) {
                console.error('Error playing music after user interaction:', err);
                scheduleRetry();
                if (releaseLock) {
                  releaseLock();
                  releaseLock = null;
                }
              }
            };
            
            userInteractionHandlerRef.current = handleUserInteraction;
            document.addEventListener('click', handleUserInteraction, { once: true });
            document.addEventListener('touchstart', handleUserInteraction, { once: true });
            document.addEventListener('keydown', handleUserInteraction, { once: true });
          }
        } else {
          console.error('Error playing music:', error);
          scheduleRetry();
        }
      }
    };

    // Retry logic with exponential backoff
    const scheduleRetry = () => {
      const now = Date.now();
      const timeSinceLastRetry = now - lastRetryTimeRef.current;
      
      // Exponential backoff: 1s, 2s, 4s, 8s, max 10s
      const maxRetries = 5;
      if (retryCountRef.current >= maxRetries) {
        console.warn('Music retry limit reached. Giving up.');
        return;
      }
      
      const backoffDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);
      
      // Don't retry too frequently
      if (timeSinceLastRetry < backoffDelay) {
        return;
      }
      
      retryCountRef.current++;
      lastRetryTimeRef.current = now;
      
      console.log(`Retrying music playback (attempt ${retryCountRef.current}/${maxRetries})...`);
      
      setTimeout(() => {
        // Only retry if music is still enabled and we should be playing
        if (musicEnabled && trackPath && getCurrentPlayingAudio() !== audio) {
          startPlaying(true).catch(err => {
            console.error('Retry failed:', err);
            if (retryCountRef.current < maxRetries) {
              scheduleRetry();
            }
          });
        }
      }, backoffDelay);
    };

    // Health check: verify music is actually playing when it should be
    // Also checks for track end (mobile devices may not loop reliably)
    const startHealthCheck = () => {
      // Clear any existing health check
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      
      healthCheckIntervalRef.current = setInterval(() => {
        // Only check if music is enabled and we have a track
        if (!musicEnabled || !trackPath) {
          return;
        }
        
        // Check if audio should be playing but isn't
        const shouldBePlaying = getCurrentPlayingAudio() === audio;
        const isActuallyPlaying = audio && !audio.paused && audio.volume > 0;
        
        // MOBILE FIX: Check if track has ended (loop might not work on mobile)
        // For looped tracks, check if we're near the end and should have looped
        const hasEnded = audio && audio.duration > 0 && 
                        (audio.currentTime >= audio.duration - 0.1 || 
                         (audio.ended && !audio.paused));
        
        // MOBILE FIX: If looped track ended, restart it
        if (hasEnded && shouldBePlaying && audio.loop) {
          console.log('Music health check: Looped track ended detected. Restarting...');
          try {
            audio.currentTime = 0;
            if (audio.paused) {
              audio.play().catch(err => {
                console.error('Error restarting looped track:', err);
                scheduleRetry();
              });
            } else {
              // If playing but at end, restart
              audio.pause();
              audio.currentTime = 0;
              audio.play().catch(err => {
                console.error('Error restarting looped track:', err);
                scheduleRetry();
              });
            }
          } catch (error) {
            console.error('Error restarting looped track:', error);
            scheduleRetry();
          }
          return;
        }
        
        if (shouldBePlaying && !isActuallyPlaying && !hasEnded) {
          console.warn('Music health check: Music should be playing but isn\'t. Attempting recovery...');
          
          // Reset retry count for health check recovery
          retryCountRef.current = 0;
          lastRetryTimeRef.current = 0;
          
          // Try to recover
          startPlaying(true).catch(err => {
            console.error('Health check recovery failed:', err);
            scheduleRetry();
          });
        }
      }, 1000); // Check every 1 second (more frequent for better mobile support)
    };

    // Try to start playing
    startPlaying();
    
    // Start health check
    startHealthCheck();

    // Cleanup function - fade out and stop
    return () => {
      // Clear health check
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      
      // Remove user interaction listener if it exists
      if (userInteractionHandlerRef.current) {
        document.removeEventListener('click', userInteractionHandlerRef.current);
        document.removeEventListener('touchstart', userInteractionHandlerRef.current);
        document.removeEventListener('keydown', userInteractionHandlerRef.current);
        userInteractionHandlerRef.current = null;
      }
      
      // Clear any active fade in interval
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
      if (fadeIntervalId) {
        clearInterval(fadeIntervalId);
        fadeIntervalId = null;
      }

      // MOBILE FIX: Remove ended event listener
      if (audio && trackEndHandlerRef.current) {
        audio.removeEventListener('ended', trackEndHandlerRef.current);
        trackEndHandlerRef.current = null;
      }

      isPlayingRef.current = false;
      retryCountRef.current = 0;
      lastRetryTimeRef.current = 0;
      
      // Release lock if held
      if (releaseLock) {
        releaseLock();
        releaseLock = null;
      }
      
      // Unregister audio element
      if (audio) {
        unregisterAudioElement(audio);
      }
      
      // Fade out this audio if it's currently playing
      if (audio && getCurrentPlayingAudio() === audio) {
        // Start fade-out and track it in global promise
        const fadeOutPromise = fadeOutAudio(audio, fadeDuration);
        setCurrentFadeOutPromise(fadeOutPromise);
        fadeOutPromise.then(() => {
          if (getCurrentPlayingAudio() === audio) {
            setCurrentPlayingAudio(null);
          }
        }).catch(() => {
          // Ignore errors in cleanup
          if (getCurrentPlayingAudio() === audio) {
            setCurrentPlayingAudio(null);
          }
        });
      } else if (audio && !audio.paused) {
        // Emergency stop if this audio is playing but not tracked
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = 0;
        } catch (e) {
          console.warn('Error stopping untracked audio:', e);
        }
      }
    };
  }, [trackPath, startTime, fadeDuration, musicEnabled]);

  // Update volume when musicVolume changes (for already playing audio)
  useEffect(() => {
    if (!audioRef.current || !musicEnabled) return;
    
    const audio = audioRef.current;
    // Only update if this audio is currently playing
    if (getCurrentPlayingAudio() === audio && !audio.paused) {
      // Smoothly transition to new volume
      const currentVolume = audio.volume;
      const targetVolume = musicVolume;
      const steps = 20;
      const volumeDiff = targetVolume - currentVolume;
      const volumeStep = volumeDiff / steps;
      let currentStep = 0;
      
      const volumeInterval = setInterval(() => {
        currentStep++;
        const newVolume = currentVolume + (volumeStep * currentStep);
        audio.volume = Math.max(0, Math.min(1, newVolume));
        
        if (currentStep >= steps) {
          clearInterval(volumeInterval);
          audio.volume = targetVolume;
        }
      }, 20);
      
      return () => clearInterval(volumeInterval);
    } else if (getCurrentPlayingAudio() === audio) {
      // If it's the current audio but paused, just set the volume directly
      audio.volume = musicVolume;
    }
  }, [musicVolume, musicEnabled]);

  return audioRef;
}

